/**
 * AcademyContextBanner, sticky pill that surfaces on Art Studio / Arcade
 * pages when the kid is browsing them from INSIDE the Learning Academy.
 *
 * Behaviour
 *   • Visible only when sessionStorage flag `academy_context_active === "1"`
 *     is set. The flag is set on entering /learn/academy/* surfaces and
 *     cleared via the "Exit Academy" button on this banner.
 *   • Hidden while already on /learn/academy/* pages (Academy already has
 *     its own back/home chrome via AcademyPageHeader).
 *   • Shows: a glowing "Inside Academy" indicator + "Back to Academy"
 *     button (returns to /learn/academy/home) + a subdued "Exit" link
 *     that clears the flag and stays on the current page.
 */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GraduationCap, ArrowLeft, X } from "lucide-react";

const FLAG_KEY = "academy_context_active";

export const setAcademyContextActive = () => {
  try { sessionStorage.setItem(FLAG_KEY, "1"); } catch (_) {}
};

export const clearAcademyContextActive = () => {
  try { sessionStorage.removeItem(FLAG_KEY); } catch (_) {}
};

const AcademyContextBanner = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState(false);

  useEffect(() => {
    try {
      setActive(sessionStorage.getItem(FLAG_KEY) === "1");
    } catch (_) {
      setActive(false);
    }
    // Listen for cross-tab changes too
    const onStorage = (e) => {
      if (e.key === FLAG_KEY) setActive(e.newValue === "1");
    };
    window.addEventListener("storage", onStorage);
    // Custom event so same-tab updates re-render
    const onLocal = () => {
      try { setActive(sessionStorage.getItem(FLAG_KEY) === "1"); } catch (_) {}
    };
    window.addEventListener("academy-context-changed", onLocal);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("academy-context-changed", onLocal);
    };
  }, [location.pathname]);

  // Hide when already inside Academy chrome
  const isInsideAcademy =
    location.pathname.startsWith("/learn/academy") ||
    location.pathname.startsWith("/academy");
  if (!active || isInsideAcademy) return null;

  const handleBack = () => {
    navigate("/learn/academy/home");
  };

  const handleExit = () => {
    clearAcademyContextActive();
    try {
      window.dispatchEvent(new Event("academy-context-changed"));
    } catch (_) {}
    setActive(false);
  };

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-[1000] pointer-events-auto"
      style={{ top: "calc(env(safe-area-inset-top, 0px) + 72px)" }}
      data-testid="academy-context-banner"
    >
      <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-gradient-to-r from-emerald-500/95 via-teal-500/95 to-cyan-500/95 shadow-[0_10px_30px_-10px_rgba(16,185,129,0.6)] border border-white/20 backdrop-blur-md animate-fadeIn">
        <span className="hidden sm:inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/20">
          <GraduationCap size={16} className="text-white" />
        </span>
        <span className="text-white text-xs sm:text-sm font-bold tracking-wide hidden xs:inline">
          Inside Learning Academy
        </span>
        <span className="text-white text-xs font-bold tracking-wide xs:hidden">
          Academy
        </span>
        <button
          type="button"
          onClick={handleBack}
          className="ml-2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/95 hover:bg-white text-emerald-700 text-xs font-bold shadow-sm transition"
          data-testid="academy-context-back"
          aria-label="Back to Learning Academy"
        >
          <ArrowLeft size={12} /> Back to Academy
        </button>
        <button
          type="button"
          onClick={handleExit}
          className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/15 hover:bg-white/30 text-white/90 transition"
          data-testid="academy-context-exit"
          aria-label="Exit Academy mode (stay on this page)"
          title="Exit Academy mode"
        >
          <X size={12} />
        </button>
      </div>
    </div>
  );
};

export default AcademyContextBanner;
